import React, { useEffect, useState, useRef, useCallback } from 'react';
import { StyleSheet, Linking } from 'react-native';
import { Box, Heading, Text, VStack, HStack, Pressable, Spinner, Center, Button, Checkbox } from 'native-base';
import AppContainer from '../Components/AppContainer';
import Trans from '../Components/Trans';
import APIAction from '../../Actions/APIAction';
import GeneralAction from '../../Actions/GeneralAction';
import main from '../../Assets/Styles/main.json';
import { createIconSetFromFontello } from 'react-native-vector-icons';
import lineAwesomeConfig from '../../Assets/Fontello/line-awesome-config.json';
import { format } from 'date-fns';
import DateTimePicker from '../../Libs/DateTimePicker';

const Icon = createIconSetFromFontello(lineAwesomeConfig);
const mainStyle = StyleSheet.create(main);

const SignedContractsScreen = ({ navigation }) => {
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    const firstLoad = useRef(true);

    // Date filters - default to current month
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const [fromDate, setFromDate] = useState(firstDayOfMonth);
    const [toDate, setToDate] = useState(today);

    // Shift-linked filter - when checked, only show contracts linked to active shifts
    const [shiftLinkedOnly, setShiftLinkedOnly] = useState(false);

    const onLoadContractsHandler = useCallback(async () => {
        // Validate date range when shift-linked filter is active
        if (shiftLinkedOnly && fromDate > toDate) {
            GeneralAction.toast('error', <Trans>From date cannot be after to date</Trans>);
            return;
        }

        try {
            setLoading(true);

            // Build query parameters
            const params = new URLSearchParams();

            // Only include date params when shift-linked filter is active
            if (shiftLinkedOnly) {
                if (fromDate) {
                    params.append('fromDate', format(fromDate, 'yyyy-MM-dd'));
                }
                if (toDate) {
                    params.append('toDate', format(toDate, 'yyyy-MM-dd'));
                }
                params.append('shiftLinkedOnly', 'true');
            }

            const response = await APIAction.request({
                method: 'GET',
                url: `/api/employee/signed-contracts?${params.toString()}`,
                cache: false,
            });

            if (!response || !response.data) {
                setContracts([]);
                return;
            }

            setContracts(response.data);
        } catch (error) {
            GeneralAction.toast('error', <Trans>Failed to load contracts</Trans>);
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, shiftLinkedOnly]);

    useEffect(() => {
        if (!firstLoad.current) {
            return;
        }

        firstLoad.current = false;
        onLoadContractsHandler();
    }, [onLoadContractsHandler]);

    const onDownloadContractHandler = async (contractId) => {
        try {
            setDownloadingId(contractId);

            const response = await APIAction.request({
                method: 'POST',
                url: `/api/contracts/${contractId}/download-link`,
                body: {
                    expiresInHours: 24,
                }
            });

            if (!response || !response.url) {
                GeneralAction.toast('error', <Trans>Failed to generate download link</Trans>);
                return;
            }

            // Open the URL in the browser
            await Linking.openURL(response.url);
        } catch (error) {
            GeneralAction.toast('error', <Trans>Failed to download contract</Trans>);
        } finally {
            setDownloadingId(null);
        }
    };

    const getTemplateName = (documentTemplate) => {
        if (!documentTemplate || !documentTemplate.name) {
            return 'Contract';
        }

        // If name is an object with language keys, try to get the right language
        if (typeof documentTemplate.name === 'object') {
            return documentTemplate.name.nl || documentTemplate.name.en || documentTemplate.name.fr || Object.values(documentTemplate.name)[0] || 'Contract';
        }

        return documentTemplate.name;
    };

    return (
        <AppContainer>
            <VStack space={2}>
                <Box style={mainStyle.pageTitleWrapper}>
                    <Box style={{ flexGrow: 1 }}>
                        <Heading style={mainStyle.pageTitle}>
                            <Trans>Signed contracts</Trans>
                        </Heading>
                    </Box>
                </Box>

                {/* Shift-linked filter checkbox */}
                <HStack space={2} alignItems={'center'} mb={2}>
                    <Checkbox
                        value="shiftLinkedOnly"
                        isChecked={shiftLinkedOnly}
                        onChange={(isChecked) => setShiftLinkedOnly(isChecked)}
                        colorScheme={'primary'}
                    >
                        <Text fontSize={'sm'} ml={2}>
                            <Trans>Only show contracts linked to active shifts</Trans>
                        </Text>
                    </Checkbox>
                </HStack>

                {/* Date Range Filters - only shown when shift-linked filter is active */}
                {shiftLinkedOnly && (
                    <HStack space={2}>
                        <VStack flex={1} space={1}>
                            <Text fontSize={'xs'} color={'gray.600'}>
                                <Trans>From Date</Trans>
                            </Text>
                            <DateTimePicker
                                mode="date"
                                value={fromDate}
                                onChange={setFromDate}
                                maximumDate={toDate}
                            />
                        </VStack>
                        <VStack flex={1} space={1}>
                            <Text fontSize={'xs'} color={'gray.600'}>
                                <Trans>To Date</Trans>
                            </Text>
                            <DateTimePicker
                                mode="date"
                                value={toDate}
                                onChange={setToDate}
                                minimumDate={fromDate}
                            />
                        </VStack>
                    </HStack>
                )}

                {/* Apply Filters Button */}
                <Button
                    colorScheme="primary"
                    size="sm"
                    onPress={onLoadContractsHandler}
                    isLoading={loading}
                >
                    <Text color={'white'} fontSize={'xs'}>
                        <Trans>Apply Filter</Trans>
                    </Text>
                </Button>

                {loading ? (
                    <Center>
                        <Spinner size="lg" />
                    </Center>
                ) : contracts.length === 0 ? (
                    <Box style={mainStyle.boxItemVertical}>
                        <Text>
                            <Trans>No signed contracts found</Trans>
                        </Text>
                    </Box>
                ) : (
                    <VStack space={2}>
                        {contracts.map((contract) => (
                            <Pressable
                                key={contract.id}
                                onPress={() => onDownloadContractHandler(contract.id)}
                                disabled={downloadingId !== null}
                            >
                                <Box
                                    style={mainStyle.boxItemVertical}
                                    bg={downloadingId === contract.id ? 'gray.100' : 'white'}
                                >
                                    <Box
                                        style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Box style={{ flex: 1 }}>
                                            <Heading style={mainStyle.mediumTitle}>
                                                {getTemplateName(contract.documentTemplate)}
                                            </Heading>
                                            {contract.client && (
                                                <Text style={{ fontSize: 13, color: 'gray.600' }}>
                                                    {contract.client.name}
                                                </Text>
                                            )}
                                            {contract.signedAt && (
                                                <Text style={{ fontSize: 12, color: 'gray.500' }}>
                                                    <Trans>Signed on</Trans>: {format(new Date(contract.signedAt), 'dd/MM/yyyy HH:mm')}
                                                </Text>
                                            )}
                                            {contract.file && (
                                                <Text style={{ fontSize: 11, color: 'gray.400' }}>
                                                    {contract.file.fileName}
                                                </Text>
                                            )}
                                        </Box>
                                        <Box style={{ paddingLeft: 10 }}>
                                            {downloadingId === contract.id ? (
                                                <Spinner size="sm" />
                                            ) : (
                                                <Icon name={'download'} size={24} color={'#3b82f6'} />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Pressable>
                        ))}
                    </VStack>
                )}
            </VStack>
        </AppContainer>
    );
};

export default SignedContractsScreen;
